// src/redux/influencerDetailsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getInfluencerDetails,
  enrichInstagram,
  enrichYoutube,
} from "../services/influencerSearchService";
import { mapApiInfluencerToUiModel } from "../mappers/influencerMapper";
import { evaluateProfileFreshness } from "../utils/profileFreshness";

/* -----------------------------
   Helpers
------------------------------ */

const FRESH_MS = 60_000;

// Progressive loading thresholds. The service may make one bounded retry
// (10s + 300ms + 18s), so the hard watchdog stays above that total while the
// UI switches to a low-cost, user-actionable state much earlier.
export const DETAIL_LOAD_SLOW_MS = 7_000;
export const DETAIL_STALE_REQUEST_MS = 12_000;
export const DETAIL_LOAD_WATCHDOG_MS = 32_000;

const toErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  return err?.message || err?.error || err?.detail || fallback;
};

const normalizeId = (value) => String(value || "").trim();

const normalizeUsername = (value) =>
  String(value || "")
    .trim()
    .replace(/^@/, "");

const normalizeArg = (arg) => {
  if (typeof arg === "string") {
    return {
      influencerId: normalizeId(arg),
      force: false,
    };
  }

  return {
    influencerId: normalizeId(arg?.influencerId),
    force: !!arg?.force,
  };
};

const isDynamoPrimaryKey = (value) => /^INF#[A-Z]+#/i.test(String(value || ""));

const resolveCanonicalId = (data) => {
  if (!data) return null;

  const platform = String(data?.platform || "").toLowerCase();

  if (platform === "instagram") {
    const rawId = data?.raw_source?.id || data?.rawSource?.id;

    return (
      normalizeId(data?.external_id) ||
      normalizeId(rawId) ||
      (!isDynamoPrimaryKey(data?.id) && /^\d+$/.test(String(data?.id || ""))
        ? normalizeId(data?.id)
        : null) ||
      normalizeUsername(data?.username) ||
      normalizeUsername(data?.handle) ||
      null
    );
  }

  if (platform === "youtube") {
    return (
      normalizeId(data?.channelId) ||
      normalizeId(data?.channel_id) ||
      normalizeId(data?.raw_source?.channel_id) ||
      normalizeId(data?.rawSource?.channel_id) ||
      normalizeId(data?.external_id) ||
      (!isDynamoPrimaryKey(data?.id) ? normalizeId(data?.id) : null) ||
      null
    );
  }

  return (
    normalizeId(data?.external_id) ||
    normalizeId(data?.channelId) ||
    normalizeId(data?.channel_id) ||
    (!isDynamoPrimaryKey(data?.id) ? normalizeId(data?.id) : null) ||
    normalizeUsername(data?.username) ||
    null
  );
};

const getRouteIdentity = (data) => {
  if (!data) return null;

  return (
    normalizeId(data?._routeId) ||
    normalizeId(data?.routeId) ||
    resolveCanonicalId(data) ||
    normalizeUsername(data?.username) ||
    null
  );
};

const looksDetailedEnough = (influencer) =>
  influencer?.details_loaded === true || influencer?.detailsLoaded === true;

const extractFirstItem = (payload) => {
  if (!payload) return null;

  if (Array.isArray(payload?.items) && payload.items.length) {
    return payload.items[0];
  }

  if (Array.isArray(payload?.data?.items) && payload.data.items.length) {
    return payload.data.items[0];
  }

  if (Array.isArray(payload?.body?.items) && payload.body.items.length) {
    return payload.body.items[0];
  }

  return null;
};

const enrichmentRoot = (payload) => {
  if (!payload || typeof payload !== "object") return {};

  if (payload.data && typeof payload.data === "object") {
    return payload.data;
  }

  if (payload.body && typeof payload.body === "object") {
    return payload.body;
  }

  return payload;
};

const stringList = (value) =>
  Array.isArray(value)
    ? value.map((item) => String(item || "").trim()).filter(Boolean)
    : [];

const includesIdentity = (values, identity) => {
  const target = normalizeUsername(identity).toLowerCase();
  if (!target) return values.length > 0;

  return values.some(
    (value) => normalizeUsername(value).toLowerCase() === target
  );
};

const inspectEnrichmentResult = (payload, identity) => {
  const root = enrichmentRoot(payload);
  const item = extractFirstItem(payload);

  const requeued = stringList(
    root.requeued_usernames ||
      root.requeuedUsernames ||
      root.requeued_channel_ids ||
      root.requeuedChannelIds
  );
  const skipped = stringList(
    root.skipped_usernames ||
      root.skippedUsernames ||
      root.skipped_channel_ids ||
      root.skippedChannelIds
  );
  const failed = stringList(
    root.failed_usernames ||
      root.failedUsernames ||
      root.failed_channel_ids ||
      root.failedChannelIds
  );

  const skipReasons = root.skip_reasons || root.skipReasons || {};
  const normalisedIdentity = normalizeUsername(identity);
  const skipReason =
    skipReasons?.[identity] ||
    skipReasons?.[normalisedIdentity] ||
    skipReasons?.[`@${normalisedIdentity}`] ||
    null;

  const saved = Number.isFinite(Number(root.saved))
    ? Number(root.saved)
    : null;

  return {
    item,
    saved,
    queued: includesIdentity(requeued, identity),
    skipped: includesIdentity(skipped, identity),
    failed: includesIdentity(failed, identity),
    skipReason,
  };
};

const enrichmentFailureMessage = (outcome, platformLabel) => {
  if (outcome.queued) {
    return `${platformLabel} refresh was queued because the provider is temporarily busy.`;
  }

  if (outcome.skipped) {
    return outcome.skipReason || `${platformLabel} refresh was skipped.`;
  }

  if (outcome.failed) {
    return `${platformLabel} could not return updated profile data.`;
  }

  return `${platformLabel} refresh completed without updated profile data.`;
};

const mapDetails = (data, routeId) => {
  const mapped = mapApiInfluencerToUiModel(data, { routeId }) || data;

  return {
    ...mapped,
    details_loaded: true,
    detail_fetched_at: new Date().toISOString(),
  };
};

const prepareDetailsPayload = (data, routeId) => {
  if (!data || typeof data !== "object") {
    throw new Error("Influencer details response was empty or invalid.");
  }

  try {
    return mapDetails(data, routeId);
  } catch (error) {
    const message = error?.message
      ? `Influencer details could not be processed: ${error.message}`
      : "Influencer details could not be processed.";

    const mappedError = new Error(message);
    mappedError.cause = error;
    throw mappedError;
  }
};

const mergeInfluencer = (current, incoming) => {
  if (!current) return incoming;
  if (!incoming) return current;

  return {
    ...current,
    ...incoming,

    // Avoid losing detail arrays when a later payload is smaller.
    recent_posts:
      incoming.recent_posts ??
      incoming.recentPosts ??
      current.recent_posts ??
      current.recentPosts ??
      undefined,

    recentPosts:
      incoming.recentPosts ??
      incoming.recent_posts ??
      current.recentPosts ??
      current.recent_posts ??
      undefined,

    raw_source: incoming.raw_source ?? current.raw_source ?? null,
    rawSource: incoming.rawSource ?? current.rawSource ?? null,
  };
};

const isSameEntity = (state, influencerId) => {
  const id = normalizeId(influencerId);

  if (!id) return false;

  return (
    normalizeId(state.routeId) === id ||
    normalizeId(state.canonicalId) === id ||
    resolveCanonicalId(state.current) === id ||
    getRouteIdentity(state.current) === id
  );
};

/* -----------------------------
   Thunks
------------------------------ */

export const fetchInfluencerDetailsAsync = createAsyncThunk(
  "influencerDetails/fetchInfluencerDetails",
  async (arg, { rejectWithValue, signal }) => {
    try {
      const { influencerId } = normalizeArg(arg);

      if (!influencerId) {
        throw new Error("influencerId is required");
      }

      const rawData = await getInfluencerDetails(influencerId, { signal });
      const data = prepareDetailsPayload(rawData, influencerId);

      return {
        influencerId,
        data,
      };
    } catch (error) {
      // Preserve createAsyncThunk cancellation semantics. Converting an abort
      // into rejectWithValue would surface a false error while the user is
      // simply navigating away or replacing a stale request.
      if (signal?.aborted || error?.name === "AbortError") {
        const abortError = new Error("Request was cancelled.");
        abortError.name = "AbortError";
        throw abortError;
      }

      return rejectWithValue(
        toErrorMessage(error, "Failed to fetch influencer details")
      );
    }
  },
  {
    condition: (arg, { getState }) => {
      const { influencerId, force } = normalizeArg(arg);

      if (!influencerId) return false;

      const state = getState()?.influencerDetails || {};

      if (
        state.requestedId === influencerId &&
        (state.loading || state.updating)
      ) {
        const startedAt = Number(state.requestStartedAt || 0);
        const requestAge = startedAt > 0 ? Date.now() - startedAt : 0;
        const canReplaceStaleRequest =
          force &&
          startedAt > 0 &&
          requestAge >= DETAIL_STALE_REQUEST_MS;

        if (!canReplaceStaleRequest) {
          return false;
        }
      }

      if (!force && isSameEntity(state, influencerId) && state.lastFetchedAt) {
        const age = Date.now() - Number(state.lastFetchedAt || 0);

        if (age < FRESH_MS && looksDetailedEnough(state.current)) {
          return false;
        }
      }

      return true;
    },
  }
);

export const refreshInfluencerAsync = createAsyncThunk(
  "influencerDetails/refreshInfluencer",
  async (
    { influencerId, platform, username, citySlug, nicheSlug },
    { rejectWithValue }
  ) => {
    try {
      const safePlatform = String(platform || "").toLowerCase();
      const safeInfluencerId = normalizeId(influencerId);
      const safeUsername = normalizeUsername(username);
      const safeCitySlug = normalizeId(citySlug) || "unknown";
      const safeNicheSlug = normalizeId(nicheSlug) || "unknown";

      if (safePlatform === "instagram") {
        if (!safeUsername) {
          throw new Error("Cannot refresh Instagram data: username not available.");
        }

        const enrichResult = await enrichInstagram({
          username: safeUsername,
          niche_slug: safeNicheSlug,
          city_slug: safeCitySlug,
          persist: true,
        });

        const outcome = inspectEnrichmentResult(enrichResult, safeUsername);

        if (!outcome.item) {
          if (outcome.queued) {
            return {
              influencerId: safeInfluencerId || safeUsername,
              data: null,
              queued: true,
              message:
                "Refresh queued. The profile will update after provider capacity is available.",
            };
          }

          throw new Error(enrichmentFailureMessage(outcome, "Instagram"));
        }

        if (outcome.saved === 0) {
          throw new Error(
            "Latest Instagram data was fetched but could not be saved. Please try again."
          );
        }

        const enrichedId = resolveCanonicalId(outcome.item);
        const resultId = enrichedId || safeInfluencerId || safeUsername;

        // The enrich endpoint returns the newly collected, persisted profile.
        // Use that response directly. An immediate details GET can read an older
        // DynamoDB/GSI version and was the reason users needed a second click.
        return {
          influencerId: resultId,
          data: prepareDetailsPayload(outcome.item, resultId),
          refreshedFrom: "enrich-result",
          queued: false,
          message: "Profile refreshed with the latest Instagram data.",
        };
      }

      if (safePlatform === "youtube") {
        const channelId = safeInfluencerId || safeUsername;

        if (!channelId) {
          throw new Error("influencerId/channel id is required for YouTube refresh");
        }

        const enrichResult = await enrichYoutube({
          channel_ids: [channelId],
          niche_slug: safeNicheSlug,
          city_slug: safeCitySlug,
          priority: 5,
        });

        const outcome = inspectEnrichmentResult(enrichResult, channelId);

        if (!outcome.item) {
          if (outcome.queued) {
            return {
              influencerId: channelId,
              data: null,
              queued: true,
              message:
                "Refresh queued. The profile will update after provider capacity is available.",
            };
          }

          throw new Error(enrichmentFailureMessage(outcome, "YouTube"));
        }

        if (outcome.saved === 0) {
          throw new Error(
            "Latest YouTube data was fetched but could not be saved. Please try again."
          );
        }

        const enrichedId = resolveCanonicalId(outcome.item);
        const resultId = enrichedId || channelId;

        return {
          influencerId: resultId,
          data: prepareDetailsPayload(outcome.item, resultId),
          refreshedFrom: "enrich-result",
          queued: false,
          message: "Profile refreshed with the latest YouTube data.",
        };
      }

      throw new Error(`Unsupported platform: ${safePlatform || "unknown"}`);
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to refresh influencer data")
      );
    }
  }
);

/* -----------------------------
   State
------------------------------ */

const initialState = {
  current: null,
  dataSource: null,
  detailsLoaded: false,
  access: null,
  refreshPolicy: null,

  routeId: null,
  canonicalId: null,
  requestedId: null,
  activeRequestId: null,
  requestStartedAt: null,

  lastFetchedAt: null,
  lastRefreshedAt: null,

  loading: false,
  updating: false,
  refreshing: false,

  error: null,
  refreshError: null,
};

/* -----------------------------
   Slice
------------------------------ */

const influencerDetailsSlice = createSlice({
  name: "influencerDetails",
  initialState,
  reducers: {
    hydrateFromSummary: (state, action) => {
      const payload = action.payload || null;
      const influencer = payload?.influencer ?? payload ?? null;
      const routeId = normalizeId(payload?.routeId);

      if (!influencer) return;

      const mappedRaw = influencer?.metrics
        ? influencer
        : mapApiInfluencerToUiModel(influencer, { routeId }) || influencer;

      // Route state is only a fast first paint. It must never be treated as an
      // authoritative details response, even when it contains several metrics.
      const mapped = {
        ...mappedRaw,
        details_loaded: false,
        detailsLoaded: false,
      };

      const incomingRouteId =
        routeId ||
        normalizeId(mapped?._routeId) ||
        normalizeId(mapped?.routeId) ||
        normalizeUsername(mapped?.username) ||
        resolveCanonicalId(mapped) ||
        null;

      const incomingCanonicalId = resolveCanonicalId(mapped);

      const sameEntity =
        !state.current ||
        isSameEntity(state, incomingRouteId) ||
        isSameEntity(state, incomingCanonicalId) ||
        resolveCanonicalId(state.current) === incomingCanonicalId;

      state.current = sameEntity ? mergeInfluencer(state.current, mapped) : mapped;

      state.routeId = incomingRouteId || state.routeId;
      state.canonicalId = incomingCanonicalId || state.canonicalId;
      state.dataSource = "summary";
      state.detailsLoaded = false;
      state.access = null;
      state.refreshPolicy = null;

      state.error = null;
      state.refreshError = null;
    },

    failStuckDetailsRequest: (state, action) => {
      const routeId = normalizeId(action.payload?.routeId);
      const requestId = normalizeId(action.payload?.requestId);

      if (routeId && normalizeId(state.routeId) !== routeId) return;
      if (
        requestId &&
        state.activeRequestId &&
        requestId !== state.activeRequestId
      ) {
        return;
      }
      if (!state.loading && !state.updating) return;

      state.loading = false;
      state.updating = false;
      state.activeRequestId = null;
      state.requestStartedAt = null;
      state.error =
        action.payload?.message ||
        "Influencer details took too long to finish loading. Please retry.";
    },

    cancelActiveDetailsRequest: (state, action) => {
      const routeId = normalizeId(action.payload?.routeId);
      const requestId = normalizeId(action.payload?.requestId);

      if (routeId && normalizeId(state.routeId) !== routeId) return;
      if (
        requestId &&
        state.activeRequestId &&
        requestId !== state.activeRequestId
      ) {
        return;
      }

      state.loading = false;
      state.updating = false;
      state.activeRequestId = null;
      state.requestStartedAt = null;
    },

    clearDetails: () => ({ ...initialState }),

    clearDetailsErrors: (state) => {
      state.error = null;
      state.refreshError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInfluencerDetailsAsync.pending, (state, action) => {
        const { influencerId } = normalizeArg(action.meta.arg);
        const sameEntity = isSameEntity(state, influencerId);

        state.requestedId = influencerId;
        state.routeId = influencerId;
        state.activeRequestId = action.meta.requestId;
        state.requestStartedAt = Date.now();

        // Critical fix: do not show the previous creator while loading a new route.
        if (!sameEntity) {
          state.current = null;
          state.canonicalId = null;
          state.dataSource = null;
          state.detailsLoaded = false;
          state.access = null;
          state.refreshPolicy = null;
        }

        // A summary/card preview is deliberately hidden behind the details
        // skeleton. Only an already-authoritative detail response may remain
        // visible during a same-route revalidation.
        state.loading = state.detailsLoaded !== true;
        state.updating = state.detailsLoaded === true;

        state.error = null;
      })

      .addCase(fetchInfluencerDetailsAsync.fulfilled, (state, action) => {
        const { influencerId, data } = action.payload || {};

        if (
          action.meta.requestId !== state.activeRequestId ||
          (state.requestedId && influencerId !== state.requestedId)
        ) {
          return;
        }

        if (!data) {
          state.loading = false;
          state.updating = false;
          state.activeRequestId = null;
          state.requestStartedAt = null;
          state.error = "Influencer details response was empty.";
          return;
        }

        const canonicalId = resolveCanonicalId(data);

        // Replace the summary atomically with the server-authorised detail
        // contract. Never merge card fields into a limited/free response.
        state.current = data;
        state.routeId = influencerId || state.routeId;
        state.canonicalId = canonicalId || state.canonicalId;
        state.dataSource = "details";
        state.detailsLoaded = true;
        state.access = data.access || null;
        state.refreshPolicy = data.refresh_policy || data.refreshPolicy || null;

        state.lastFetchedAt = Date.now();
        state.loading = false;
        state.updating = false;
        state.activeRequestId = null;
        state.requestStartedAt = null;
        state.error = null;
      })

      .addCase(fetchInfluencerDetailsAsync.rejected, (state, action) => {
        // Ignore completions from cancelled/replaced requests. This prevents an
        // old route or retry from overwriting the active creator state.
        if (action.meta.requestId !== state.activeRequestId) {
          return;
        }

        state.loading = false;
        state.updating = false;
        state.activeRequestId = null;
        state.requestStartedAt = null;

        if (action.meta.aborted) {
          state.error = null;
          return;
        }

        state.error = action.payload || "Failed to fetch influencer details.";
      })

      .addCase(refreshInfluencerAsync.pending, (state) => {
        state.refreshing = true;
        state.refreshError = null;
      })

      .addCase(refreshInfluencerAsync.fulfilled, (state, action) => {
        const { data, influencerId: refetchedId } = action.payload || {};

        if (data) {
          const routeId = state.routeId || refetchedId;
          const mapped = data;
          const canonicalId = resolveCanonicalId(mapped);
          const observedAt =
            mapped.metrics_observed_at ||
            mapped.metricsObservedAt ||
            mapped.source_refreshed_at ||
            mapped.sourceRefreshedAt ||
            mapped.last_refreshed_at ||
            mapped.lastRefreshedAt ||
            null;

          const freshness = evaluateProfileFreshness({
            ...mapped,
            metrics_observed_at: observedAt,
            metricsObservedAt: observedAt,
          });

          state.current = mergeInfluencer(state.current, {
            ...mapped,
            details_loaded: true,
            detailsLoaded: true,
            metrics_observed_at: observedAt,
            metricsObservedAt: observedAt,
            freshness_status: freshness.status,
            freshnessStatus: freshness.status,
          });
          state.dataSource = "details";
          state.detailsLoaded = true;
          state.access = mapped.access || state.access;
          state.refreshPolicy =
            mapped.refresh_policy || mapped.refreshPolicy || state.refreshPolicy;
          state.canonicalId =
            canonicalId ||
            resolveCanonicalId({ id: refetchedId, platform: mapped?.platform }) ||
            state.canonicalId;

          state.lastFetchedAt = Date.now();
          // This state records when metrics were actually observed. Never replace
          // a missing provider timestamp with the browser clock.
          state.lastRefreshedAt = observedAt || state.lastRefreshedAt;
        }

        state.refreshing = false;
        state.refreshError = null;
        state.error = null;
      })

      .addCase(refreshInfluencerAsync.rejected, (state, action) => {
        state.refreshing = false;
        state.refreshError =
          action.payload || "Failed to refresh influencer data.";

        // Keep existing page visible; surface refresh failure separately.
        state.error = state.error || null;
      });
  },
});

export const {
  hydrateFromSummary,
  failStuckDetailsRequest,
  cancelActiveDetailsRequest,
  clearDetails,
  clearDetailsErrors,
} = influencerDetailsSlice.actions;

export default influencerDetailsSlice.reducer;